import React, { useState, useEffect } from 'react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import '../styles/Navbar.css';
import logo from '../assets/logo.png';
import { FaBars, FaTimes, FaChevronDown } from 'react-icons/fa';
import Dropdown from './Dropdown';

const Navbar = () => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('home');

  const location = useLocation();
  const navigate = useNavigate();

  const handleDropdownToggle = () => {
    setDropdownOpen((prev) => !prev);
  };

  const closeMobileMenu = () => {
    setMenuOpen(false);
    setDropdownOpen(false);
  };

  // Track scroll position to determine active section
  useEffect(() => {
    const handleScroll = () => {
      if (location.pathname === '/') {
        const sections = ['home', 'about', 'services', 'contact'];
        let currentSection = 'home';
        const scrollPosition = window.scrollY + 100;

        for (const section of sections) {
          const element = document.getElementById(section);

          if (element) {
            const offsetTop = element.offsetTop;
            const offsetBottom = offsetTop + element.offsetHeight;

            if (
              scrollPosition >= offsetTop &&
              scrollPosition < offsetBottom
            ) {
              currentSection = section;
              break;
            }
          }
        }

        if (scrollPosition < 100) {
          currentSection = 'home';
        }

        setActiveSection(currentSection);
      }
    };

    // Set initial active state based on current route
    if (location.pathname !== '/') {
      setActiveSection('');
    } else {
      handleScroll();
    }

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [location.pathname]);

  // Scroll helper: Go to home and then scroll
  const scrollToSection = (sectionId) => {
    if (location.pathname !== '/') {
      navigate('/');

      setTimeout(() => {
        const section = document.getElementById(sectionId);

        if (section) {
          section.scrollIntoView({
            behavior: 'smooth',
          });

          setActiveSection(sectionId);
        }
      }, 500);
    } else {
      const section = document.getElementById(sectionId);

      if (section) {
        section.scrollIntoView({
          behavior: 'smooth',
        });

        setActiveSection(sectionId);
      }
    }

    closeMobileMenu();
  };

  // Check if a nav item is active
  const isActive = (itemType, value) => {
    if (itemType === 'route') {
      return location.pathname === value;
    }

    if (itemType === 'section') {
      return (
        location.pathname === '/' &&
        activeSection === value
      );
    }

    return false;
  };

  // Handle Home click
  const handleHomeClick = () => {
    if (location.pathname !== '/') {
      navigate('/');
    } else {
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    }

    setActiveSection('home');
    closeMobileMenu();
  };

  return (
    <nav className="navbar">

      {/* Logo and Brand */}
      <div className="navbar-left">
        <img
          src={logo}
          alt="Dani Import & Export Trading Logo"
          className="logo"
        />

        <span className="brand-name">
          DANI IMPORT & EXPORT TRADING
        </span>
      </div>

      {/* Navigation */}
      <div className="navbar-right">

        {/* Mobile Menu Button */}
        <div
          className="nav-toggle"
          onClick={() => setMenuOpen(!menuOpen)}
        >
          {menuOpen ? (
            <FaTimes size={22} color="#fff" />
          ) : (
            <FaBars size={22} color="#fff" />
          )}
        </div>

        <ul className={`nav-links ${menuOpen ? 'show' : ''}`}>

          {/* Home */}
          <li>
            <RouterLink
              to="/"
              onClick={handleHomeClick}
              className={
                isActive('route', '/')
                  ? 'active'
                  : ''
              }
            >
              Home
            </RouterLink>
          </li>

          {/* About */}
          <li>
            <button
              className={`nav-btn ${
                isActive('section', 'about')
                  ? 'active'
                  : ''
              }`}
              onClick={() => scrollToSection('about')}
            >
              About
            </button>
          </li>

          {/* Services */}
          <li>
            <button
              className={`nav-btn ${
                isActive('section', 'services')
                  ? 'active'
                  : ''
              }`}
              onClick={() => scrollToSection('services')}
            >
              Services
            </button>
          </li>

          {/* Portfolio */}
          <li>
            <RouterLink
              to="/portfolio"
              onClick={closeMobileMenu}
              className={
                isActive('route', '/portfolio')
                  ? 'active'
                  : ''
              }
            >
              Portfolio
            </RouterLink>
          </li>

          {/* Contact */}
          <li>
            <button
              className={`nav-btn ${
                isActive('section', 'contact')
                  ? 'active'
                  : ''
              }`}
              onClick={() => scrollToSection('contact')}
            >
              Contact
            </button>
          </li>

          {/* Community Dropdown */}
          <li
            className="more"
            onClick={handleDropdownToggle}
          >
            <span
              className={`more-text ${
                isActive('route', '/team') ||
                isActive('route', '/testimonials')
                  ? 'active'
                  : ''
              }`}
            >
              Community

              <FaChevronDown className="dropdown-icon" />
            </span>

            {dropdownOpen && <Dropdown />}
          </li>

          {/* Digital Products */}
          <li>
            <RouterLink
              to="/digital-products"
              onClick={closeMobileMenu}
              className={
                isActive('route', '/digital-products')
                  ? 'active'
                  : ''
              }
            >
              Digital Products
            </RouterLink>
          </li>

        </ul>
      </div>
    </nav>
  );
};

export default Navbar;